import React, {createElement, useState, useRef, useCallback} from 'react';
import type {CSSProperties, ReactNode} from 'react';
import mergeRefs from 'merge-refs';
import {withCssModule} from '@pb/kit-util-with-css-module';

import {
    ALIGN_CLASSES,
    TEXT_ALIGN_CLASSES,
    resolveTextAlign,
    resolveCellVerticalAlign,
} from './constants';
import type {VerticalAlignType} from './constants';

import styles from './table.module.styl';

const classNames = withCssModule(styles);

/** @deprecatedType Passing `true` (boolean) is deprecated and will be removed in 6 months 03-03-2026.
 *  Use a string value instead: `title="cell text"`. */
type CellTitleFlag = boolean;

export type CellProps<T extends 'th' | 'td' = 'td'> = {
    /**
     * Renders as 'th' or 'td'. Default: 'td'
     */
    as?: T,
    /**
     * @private
     * If cell in table head
     * @deprecated Use `as='th'` instead. It will be removed in 6 months 03-03-2026.
     */
    th?: boolean,
    /**
     * Subtitle cell (adds caption styling)
     * @deprecated Create custom layouts on the consumer side instead. It will be removed in 6 months 03-03-2026.
     */
    caption?: ReactNode,
    /**
     * Block type — no padding
     */
    block?: boolean,
    /**
     * Column for numbers
     */
    number?: boolean,
    /**
     * Height of the cell (applied as inline style)
     */
    height?: string | number,
    /**
     * Width of the cell (applied as inline style)
     */
    width?: string | number,
    /**
     * Show/hide cell border
     * @default true
     */
    border?: boolean,
    /**
     * White space should be normal
     * @deprecated Use global CSS class `white-space-normal` instead. It will be removed in 6 months 03-03-2026.
     */
    whiteSpaceNormal?: boolean,
    /**
     * Title tooltip shown on hover.
     */
    title?: CellTitleFlag | string,
    /**
     * Text alignment.
     * @deprecated Use global CSS classes `text-align`, `text-align_center`, `text-align_right` via `className` instead. It will be removed in 6 months 03-03-2026.
     */
    align?: string,
    /**
     * Vertical alignment. Use lowercase values: 'top' | 'middle' | 'baseline' | 'bottom'.
     * Old VerticalAlign enum values (e.g. VerticalAlign.TOP) are deprecated and will be removed in 6 months 03-03-2026.
     */
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    verticalAlign?: 'default' | 'top' | 'middle' | 'baseline' | 'bottom' | VerticalAlignType,
    /**
     * Set default cell padding
     */
    defaultCellPadding?: boolean,
    /**
     * Disable text wrapping
     * @deprecated Use global CSS class `nowrap` instead. It will be removed in 6 months 03-03-2026.
     */
    nowrap?: boolean,
    /**
     * Thick right border
     */
    separated?: boolean,
    /**
     * Mix for Cell
     * @deprecated Use `className` instead. It will be removed in 6 months 03-03-2026.
     */
    mix?: string,
} & Omit<React.ComponentPropsWithRef<T>, 'title' | 'align' | 'height' | 'width'>;

export const Cell = <T extends 'th' | 'td' = 'td'>({
    as,
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    th,
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    caption,
    block,
    number,
    height,
    width,
    border = true,
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    whiteSpaceNormal,
    title,
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    align,
    verticalAlign,
    defaultCellPadding,
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    nowrap,
    separated,
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    mix,
    className,
    style,
    children,
    ref,
    onMouseEnter,
    ...rest
}: CellProps<T>) => {
    const cellRef = useRef<HTMLTableCellElement>(null);
    const [resolvedTitle, setResolvedTitle] = useState<string | null>(null);

    const handleMouseEnter = useCallback((e: React.MouseEvent<HTMLTableCellElement>) => {
        if (title) {
            setResolvedTitle(prev => {
                if (typeof title === 'string') {
                    return prev === title ? prev : title;
                }
                const textContent = cellRef.current?.textContent ?? '';
                return prev === textContent || !textContent ? prev : textContent;
            });
        }
        onMouseEnter?.(e);
    }, [title, onMouseEnter]);

    const tag = as ?? (th ? 'th' : 'td');
    const resolvedTextAlign = resolveTextAlign(undefined, align as string | undefined);
    const resolvedVerticalAlign = resolveCellVerticalAlign(verticalAlign);

    const cellClassName = classNames(
        'table__col',
        mix,
        className,
        {
            'table__col_caption': caption || caption === '',
            'table__col_block': block,
            'table__col_number': number,
            'table__col_nowrap': nowrap || height,
            'table__col_white-space-normal': whiteSpaceNormal,
            'table__col_default-padding': defaultCellPadding,
            'table__col_separated': separated,
        },
        resolvedTextAlign ? TEXT_ALIGN_CLASSES[resolvedTextAlign] : null,
        ALIGN_CLASSES('table__col')[resolvedVerticalAlign],
    );

    const cellStyle: CSSProperties = {
        ...(height ? {height} : {}),
        ...(width ? {width} : {}),
        ...(!border ? {border: 'none'} : {}),
        ...style,
    };

    let content: ReactNode;
    if (caption) {
        content = <span>{caption}</span>;
    } else if (caption === '' || children == null) {
        content = <span>&nbsp;</span>;
    } else {
        content = children;
    }

    if (title) {
        content = <span title={resolvedTitle ?? undefined} className="ellipsis">{content}</span>;
    }

    return createElement(tag, {
        ...rest,
        ref: mergeRefs(ref, cellRef),
        className: cellClassName,
        style: cellStyle,
        onMouseEnter: title !== undefined ? handleMouseEnter : onMouseEnter,
    }, content);
};
